import React, { useEffect, useRef, useState } from 'react';

const HERO_SLOGAN_INTERVAL_MS = 5500;
const HERO_SLOGAN_LINE_STAGGER_MS = 380;

export function getHeroCopyFrameStyle(isMobile) {
  return isMobile
    ? { paddingLeft: '1rem', paddingRight: '1rem' }
    : { paddingLeft: '28%', paddingRight: 'clamp(2rem, 5vw, 6rem)' };
}

function HeroSloganLine({
  text,
  offsetX,
  align,
  isMobile,
  textColor,
  lineIndex,
  cycleKey,
  reducedMotion,
}) {
  const fontSize = isMobile ? '2.5rem' : 'clamp(2.75rem, 5.5vw, 5.25rem)';

  return (
    <div
      style={{
        marginBottom: lineIndex === 0 && isMobile ? '12px' : undefined,
        marginTop: lineIndex === 1 && isMobile ? '6px' : undefined,
        minHeight: isMobile ? '2.85rem' : 'clamp(3.1rem, 6.2vw, 5.9rem)',
      }}
    >
      <span
        className={`block font-mono ${textColor} whitespace-pre-line leading-[1.12] tracking-tight`}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: 'transform 0.3s cubic-bezier(.4,0,.2,1)',
          textAlign: align,
          fontSize,
        }}
      >
        <span
          key={cycleKey}
          className={`block ${reducedMotion ? '' : 'hero-slogan-clock-line--animate'}`}
          style={{
            animationDelay: reducedMotion ? undefined : `${lineIndex * HERO_SLOGAN_LINE_STAGGER_MS}ms`,
          }}
        >
          {text}
        </span>
      </span>
    </div>
  );
}

export function HeroRotatingSlogan({
  slogans,
  isMobile,
  textColor,
  line1OffsetX,
  line2OffsetX,
  reducedMotion,
}) {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
  }, [slogans]);

  useEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2 }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isInView || reducedMotion || slogans.length <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % slogans.length);
    }, HERO_SLOGAN_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [isInView, reducedMotion, slogans.length]);

  const activeSlogan = slogans[activeIndex] ?? slogans[0];
  const align = isMobile ? 'left' : 'right';

  if (!activeSlogan) {
    return null;
  }

  const copyFrameStyle = getHeroCopyFrameStyle(isMobile);

  return (
    <div
      ref={containerRef}
      className={
        isMobile
          ? 'relative z-30 flex min-h-[13svh] w-full flex-col justify-start pt-[1svh] pb-[5svh] pointer-events-none -mt-[1.5svh]'
          : 'relative z-30 flex min-h-[15svh] w-full items-start justify-end pointer-events-none pt-[1svh] pb-[8svh] -mt-[3svh]'
      }
      style={copyFrameStyle}
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="w-full" role="status">
        <HeroSloganLine
          text={activeSlogan.lines[0]}
          offsetX={line1OffsetX}
          align={align}
          isMobile={isMobile}
          textColor={textColor}
          lineIndex={0}
          cycleKey={`${activeIndex}-0`}
          reducedMotion={reducedMotion}
        />
        <HeroSloganLine
          text={activeSlogan.lines[1]}
          offsetX={line2OffsetX}
          align={align}
          isMobile={isMobile}
          textColor={textColor}
          lineIndex={1}
          cycleKey={`${activeIndex}-1`}
          reducedMotion={reducedMotion}
        />
      </div>
    </div>
  );
}
